import React, { useEffect, useContext } from 'react';
import { ColorSelector } from 'web-store-modules/packages/product-modal';
import ColorDropdownSelector from './color-dropdown-selector/ColorDropdownSelector';

import ProductModalFooter from './ProductModalFooter.jsx';
import ProductModalCarousel from './carousel/ProductModalCarousel.jsx';
import ProductModalButtons from './ProductModalButtons.jsx';
import ProductModalDetails from './ProductModalDetails.jsx';

import { ProductModalContext } from '../../ProductModalContext';
import { formatPriceString } from '../util/ProductModalHelpers';
import { ContentTypes } from '../util/ProductModalEnums.js';
import { SpinActivated, ColorSelectorAnalytics } from '../../../../../../utils/Analytics';
import { AppContext } from '../../../../../../AppContext.jsx';
import { isMobileDevice } from 'web-store-modules/packages/obsess-device-detector';
import I18NextContext from '../../../../../ui/i18next-context/I18NextContext'
import SizeSelector from './size-selector/SizeSelector';
import QuantitySelector from './quantity-selector/QuantitySelector';
import ProductModalButtonsNew from './ProductModalButtonsNew.jsx';
import { constructUrl } from '../../../../../../utils/UrlConstructor';
import ProductWishlistButton from './ProductWishlistButton';
import CuratedForYou from '../../shop-the-look-modal/CuratedForYou/CuratedForYou';
import WidthSelector from './width-selector/WidthSelector';

const isMobile = isMobileDevice();
const fragranceColorRemove = ["74784800000", "51483400000", "74824700000", "72992600000", "70910900000"];

const loadBodyContent = (WrappedComponent) => {
    return (props) => {
        const {
            isLoading,
            productInfo,
            modalStylingFields,
            fontStyles,
            productPricePrefix,
            thumbnailUrls,
            productSKU,
            groupProducts,
            fetchProductInfo,
            onColorSelectorLoaded,
            primaryColor,
            secondaryColor,
            showingAddToCartPrompt,
            setShowingAddToCartPrompt,
            addToCartEnabled,
            focusMode,
            setFocusMode,
            variantSize,
            setVariantSize,
            variantColor,
            setVariantColor,
            productVariantMap,
            colors,
            setSelectedVariantSKU,
            relatedProducts,
            variantWidth,
            setVariantWidth,
            setShowSizeGuide
        } = useContext(ProductModalContext);
        const { currentLanguage } = useContext(I18NextContext);
        const { setLoadingIconVisibility } = useContext(AppContext);

        useEffect(() => {
            setLoadingIconVisibility(isLoading);
        }, [isLoading])

        const {
            showBrandName,
            showColor,
            showDescription,
            showPrice
        } = modalStylingFields;

        const { brandNameFontStyle, popUpTitleFontStyle, colorFontStyle, shortDescriptionFontStyle, priceFontStyle } = fontStyles;
        const [priceNum, salePriceNum] = [productInfo[ContentTypes.PRICE], productInfo[ContentTypes.SALE_PRICE]].map(price => {
            if (price || price === 0) {
                if (price % 1 !== 0) {
                    return price.toFixed(2);
                }
            }
            return price;
        });


        const onSpinActivated = () => {
            const { sku, title } = props;
            SpinActivated(sku, title);
        }

        const getProductThumbnailUrl = () => {
            if (productInfo[ContentTypes.DOWNSIZED_CAROUSEL_IMAGES])
                return productInfo[ContentTypes.DOWNSIZED_CAROUSEL_IMAGES]['carousel'][0]
            else if (productInfo[ContentTypes.CAROUSEL_IMAGES]['carousel'][0])
                return constructUrl(productInfo[ContentTypes.CAROUSEL_IMAGES]['carousel'][0])
            else
                return productInfo[ContentTypes.DOWNSIZED_THUMBNAIL];
        }

        const responsiveTitleStyle = {
            ...popUpTitleFontStyle,
            fontSize: popUpTitleFontStyle.fontSize,
        }
        const shortDescriptionStyle = { ...shortDescriptionFontStyle }
        if (priceNum === undefined) {
            responsiveTitleStyle.marginBottom = '5px';
            shortDescriptionStyle.marginBottom = '10px';
        }
        const priceFontUpdated = {
            ...priceFontStyle,
            fontSize: '13px',
            color: '#000000',
            fontWeight: 'bold',
            lineHeight: '15.6px'
        }
        const productGroup = productInfo[ContentTypes.GROUP];
        const brand = showBrandName && <span style={brandNameFontStyle}>{productInfo[ContentTypes.BRAND]}</span>;
        const title = showBrandName && <span style={responsiveTitleStyle}>{fragranceColorRemove.includes(productInfo[ContentTypes.SKU]) ? productInfo[ContentTypes.TITLE].replace('Incolore', '').replace('Incoloro', '') : productInfo[ContentTypes.TITLE]}</span>;
        const color = showColor && !productGroup && <span style={colorFontStyle}>{productInfo[ContentTypes.COLOR]}</span>;
        const description = showDescription && <span style={shortDescriptionStyle}>{productInfo[ContentTypes.DESCRIPTION]}</span>;
        const price = showPrice && priceNum !== undefined && priceNum > 0 && <span style={priceFontUpdated}>{formatPriceString(priceNum, currentLanguage)}</span>;
        const salePrice = showPrice && salePriceNum && salePriceNum > 0 && <span style={priceFontUpdated}>{formatPriceString(salePriceNum, productPricePrefix)}</span>;
        // size dropdown enabled if there is size info populated in the CSV
        const sizeDropdownEnabled = !!productVariantMap.features_map;
        const groupColors = (Object.keys(productVariantMap.features_map).length > 0) && Object.keys(productVariantMap.features_map); // array of color variants for a SKU
        // const colorSwatchesEnabled = productVariantMap.siblings && !!productVariantMap.siblings[productSKU].color_image_file;
        let colorSelector = null;
        let customWidth = null;
        let groupProductWidth = {};
        const enabledWidth = productInfo[ContentTypes.CUSTOM_FIELD];
        if (enabledWidth) {
            const sizeObject = productVariantMap.features_map[variantColor] || {};
            for (const [key, value] of Object.entries(sizeObject)) {
                groupProductWidth[key] = value;
            }

            customWidth = <WidthSelector
                sku={productSKU}
                variantSize={variantSize}
                variantWidth={variantWidth}
                setVariantWidth={setVariantWidth}
                groupProductWidth={groupProductWidth[variantSize]}
                />
        }

        // if there is a productGroup - then there are variants (color, size, or both)
        if (groupColors) {
            // using color dropdown - CSV data has color names and no color files
            colorSelector = <ColorDropdownSelector
                fontStyle={fontStyles.colorFontStyle}
                defaultColor={productVariantMap.color}
                groupColors={groupColors}
            />
        } else {
            // no color swatches or dropdowns
            colorSelector = <div style={{ padding: focusMode ? '0' : '0.5em 0' }} />
        }

        const sizeSelector = sizeDropdownEnabled && (
            <SizeSelector
            sku={productSKU}
            groupProducts={groupProducts}
            variantSize={variantSize}
            setVariantSize={setVariantSize}
            colors={colors}
            variantColor={variantColor}
            productVariantMap={productVariantMap}
            setVariantColor={setVariantColor}
            setSelectedVariantSKU={setSelectedVariantSKU}
        />
        )

        const qtySelector = <QuantitySelector title={productInfo[ContentTypes.TITLE]} sku={productSKU} />

        const productWishlistBtn = <ProductWishlistButton
            sku={productSKU}
            title={productInfo[ContentTypes.TITLE]}
            price={productInfo[ContentTypes.PRICE]}
            downsizedThumbnail={getProductThumbnailUrl()}
            sizeDropdownEnabled={sizeDropdownEnabled}
        />

        const productButtonsNew = !focusMode && (
            <ProductModalButtonsNew
                buttonFontStyle={fontStyles.buttonTextFontStyle}
                productButtonCopy={modalStylingFields.productButtonCopy}
                addToCartCopy={modalStylingFields.addToCartCopy}
                sku={productSKU}
                title={productInfo[ContentTypes.TITLE]}
                price={productInfo[ContentTypes.PRICE]}
                downsizedThumbnail={getProductThumbnailUrl()}
                url={productInfo[ContentTypes.URL]}
                // cartUrl={productInfo[ContentTypes.CUSTOM_FIELD]}
                colorName={productInfo[ContentTypes.COLOR]}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                buttonBorderRadius='1em'
                showingAddToCartPrompt={showingAddToCartPrompt}
                setShowingAddToCartPrompt={setShowingAddToCartPrompt}
                addToCartEnabled={addToCartEnabled}
                addToCartFontStyle={fontStyles.addToCartTextFontStyle}
                sizeDropdownEnabled={sizeDropdownEnabled}
                groupProducts={groupProducts}
                fetchProductInfo={fetchProductInfo}
                onClose={props.onClose}
            />
        )
        
        const curatedForYou = relatedProducts.length ? <CuratedForYou relatedProductInfo={relatedProducts} fragranceColorRemove={fragranceColorRemove} /> : null;

        const productImage = <ProductModalCarousel priceNum={priceNum} sku={productSKU} thumbnailArr={thumbnailUrls} onSpinActivated={onSpinActivated} focusMode={focusMode} setFocusMode={setFocusMode} productInfo={productInfo} />;

        const productButtons = !focusMode && (
            <ProductModalButtons
                buttonFontStyle={fontStyles.buttonTextFontStyle}
                productButtonCopy={modalStylingFields.productButtonCopy}
                addToCartCopy={modalStylingFields.addToCartCopy}
                sku={productSKU}
                title={productInfo[ContentTypes.TITLE]}
                price={productInfo[ContentTypes.PRICE]}
                downsizedThumbnail={getProductThumbnailUrl()}
                url={productInfo[ContentTypes.URL]}
                // cartUrl={productInfo[ContentTypes.CUSTOM_FIELD]}
                colorName={productInfo[ContentTypes.COLOR]}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                buttonBorderRadius='1em'
                showingAddToCartPrompt={showingAddToCartPrompt}
                setShowingAddToCartPrompt={setShowingAddToCartPrompt}
                addToCartEnabled={addToCartEnabled}
                addToCartFontStyle={fontStyles.addToCartTextFontStyle}
                sizeDropdownEnabled={sizeDropdownEnabled}
                groupProducts={groupProducts}
                fetchProductInfo={fetchProductInfo}
            />
        )
        // }

        const productDetails = (productInfo[ContentTypes.DETAILS_BODY_1] || productInfo[ContentTypes.DETAILS_BODY_2]) && <ProductModalDetails />

        // A map of all the components to be referenced when a load order is passed into render product content.
        const componentMap = {
            brand,
            title,
            color,
            description,
            price,
            salePrice,
            colorSelector,
            productImage,
            productButtons,
            productDetails,
            sizeSelector,
            qtySelector,
            productButtonsNew,
            productWishlistBtn,
            curatedForYou,
            customWidth
        }

        const footer = (productInfo[ContentTypes.DETAILS_BODY_1] || productInfo[ContentTypes.DETAILS_BODY_2]) && (
            <ProductModalFooter />
        )

        // TODO: Once CMS functionality is defined we can retrieve load order directly from this component, or through context
        const renderProductContent = (loadOrder) => {
            return loadOrder.map((field, index) => {
                return (
                    <React.Fragment key={index}>
                        {componentMap[field]}
                    </React.Fragment>
                );
            });
        }

        return <WrappedComponent renderProductContent={renderProductContent} footer={footer} {...props} />
    }
}

export default loadBodyContent;
